import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'productAccessOptions',
    'prescriptionWrapper',
    'prescriptionContainer',
    'productSelect',
    'prescriptionErrors',
    'prescriptionOnCreate',
    'productsToDelete',
    'editAccess',
    'persistAccess',
    'patientInfo',
    'prescriptionItem',
    'quantityInput',
    'fullProductAccess',
    'prescriptionReminder',
    'accessExpiresIn',
    'accessExpiresDate',
    'individualAccessHelper',
  ];

  static NO_EXPIRE_OPTION = 7;
  static values = {
    hasUnsavedChanges: Boolean,
  };

  connect() {
    this.boundDpChangeActions ||= this.dpChangeActions.bind(this);
    this.element.addEventListener('change', this.boundDpChangeActions);

    if (this.hasProductAccessOptionsTarget) {
      this.originalContent = this.productAccessMarkup();
    }

    if (this.hasPrescriptionOnCreateTarget) {
      this.markAsChanged();
    }

    this.handleBeforeUnload = this._beforeUnload.bind(this);
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    this.setupExpirationFields();
  }

  // Tom Select rewrites its select in place, so snapshot the section without the
  // generated wrappers — cancelProductAccess restores this and Stimulus re-attaches.
  productAccessMarkup() {
    const clone = this.productAccessOptionsTarget.cloneNode(true);

    clone.querySelectorAll('.ts-wrapper').forEach((el) => el.remove());
    clone.querySelectorAll('select.tomselected').forEach((el) => {
      el.classList.remove('tomselected', 'ts-hidden-accessible');
      el.removeAttribute('tabindex');
    });

    return clone.innerHTML;
  }

  setupExpirationFields() {
    for (const prescription of this.prescriptionItemTargets) {
      const expiresIn = this.accessExpiresInTargets.find((el) =>
        prescription.contains(el),
      );
      const expiresDate = this.accessExpiresDateTargets.find((el) =>
        prescription.contains(el),
      );

      if (expiresDate?.value === '') {
        expiresIn.selectedIndex = this.constructor.NO_EXPIRE_OPTION;
      } else {
        expiresIn.selectedIndex = -1;
      }
    }
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  markAsChanged() {
    this.hasUnsavedChangesValue = true;
  }

  clearChanges() {
    this.hasUnsavedChangesValue = false;
  }

  _beforeUnload(event) {
    if (this.hasUnsavedChangesValue) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  toggleProductAccess(e) {
    this.editAccessTarget.classList.toggle('hide');
    this.persistAccessTarget.classList.toggle('hide');

    if (
      this.productAccessOptionsTarget.classList.contains('disabled-section')
    ) {
      this.productAccessOptionsTarget.classList.remove('disabled-section');
      this.patientInfoTargets.forEach((el) =>
        el.classList.add('disabled-section'),
      );
    } else {
      this.productAccessOptionsTarget.classList.add('disabled-section');
      this.patientInfoTargets.forEach((el) =>
        el.classList.remove('disabled-section'),
      );
    }
  }

  disableSavedProductFields() {
    this.prescriptionWrapperTarget
      .querySelectorAll('select[data-controller~="select"]')
      .forEach((select) => {
        select.disabled = true;
        select.tomselect?.disable();
      });

    this.quantityInputTargets.forEach((input) => {
      input.disabled = true;
    });
  }

  togglePrescription(e) {
    const access = e.target.dataset['access'];

    if (access === 'full' || access === 'practitioner_only') {
      this.prescriptionContainerTarget.classList.add('hide');
    } else {
      this.prescriptionContainerTarget.classList.remove('hide');
    }

    if (this.hasIndividualAccessHelperTarget) {
      this.individualAccessHelperTarget.classList.toggle(
        'hide',
        access !== 'practitioner_only',
      );
    }
  }

  addPrescription(withDivider = true) {
    let emptySelect = null;

    for (const select of this.prescriptionWrapperTarget.querySelectorAll(
      'select[data-controller~="select"]:not([disabled])',
    )) {
      if (!select.value) {
        emptySelect = select;
        break;
      }
    }

    if (emptySelect && withDivider) {
      this.prescriptionErrorsTarget.innerHTML = '';

      this.prescriptionErrorsTarget.insertAdjacentHTML(
        'beforeend',
        `
        <p>
          Product can't be blank
        </p>
      `,
      );

      const selectWrapper = emptySelect.nextElementSibling;
      if (selectWrapper && selectWrapper.classList.contains('ts-wrapper')) {
        selectWrapper.classList.add('is-invalid');
      }

      return;
    }

    this.prescriptionErrorsTarget.innerHTML = '';

    const clonedDiv = this.clonePrescriptionDiv();

    if (withDivider) {
      const dividerDiv = this.createDivider(clonedDiv.dataset['index']);
      this.prescriptionWrapperTarget.appendChild(dividerDiv);
    }

    this.prescriptionWrapperTarget.appendChild(clonedDiv);
  }

  removePrescription(e) {
    if (this.prescriptionItemTargets.length === 1) {
      this.addPrescription(false);
    }

    const prescriptionDiv = e.target.closest('div.prescription-container');
    const prescriptions = this.prescriptionItemTargets;
    let index = prescriptionDiv.dataset['index'];

    if (prescriptions[0].dataset['index'] === index) {
      index = prescriptions[1].dataset['index'];
    }

    const productSelect = prescriptionDiv.querySelector(
      'select[name="bio_patient_form[product_id]"]',
    );
    const selectedProductId = productSelect.value;

    const selectedOption = productSelect.options[productSelect.selectedIndex];
    const selectedProductText = selectedOption ? selectedOption.text : '';

    this.updateHiddenField(selectedProductId);

    if (selectedProductId && selectedProductText) {
      this.prescriptionWrapperTarget
        .querySelectorAll('select[data-controller~="select"]:not([disabled])')
        .forEach((select) => {
          if (
            !Array.from(select.options).some(
              (option) => option.value === selectedProductId,
            )
          ) {
            const newOption = document.createElement('option');
            newOption.value = selectedProductId;
            newOption.text = selectedProductText;

            select.add(newOption);

            select.tomselect?.sync();
          }
        });
    }

    prescriptionDiv.parentNode.removeChild(prescriptionDiv);

    const hrDiv = this.prescriptionWrapperTarget.querySelector(
      `[data-index="${index}"][data-name="divider"]`,
    );

    if (hrDiv) {
      hrDiv.parentNode.removeChild(hrDiv);
    }
  }

  updateHiddenField(value) {
    let currentValue = this.productsToDeleteTarget.value;

    let valuesArray = currentValue ? JSON.parse(currentValue) : [];

    if (!valuesArray.includes(value)) {
      valuesArray.push(value);
    }

    this.productsToDeleteTarget.value = JSON.stringify(valuesArray);
  }

  calculateAccessExpires(e) {
    const prescriptionContainer = e.target.closest('.prescription-container');
    const accessExpires = this.accessExpiresDateTargets.find((el) =>
      prescriptionContainer.contains(el),
    );
    const selected = e.target.options[e.target.selectedIndex];

    accessExpires.classList.remove('is-invalid');
    accessExpires.value = selected.dataset.expiresOn ?? '';
  }

  disableProductAccessSection(e) {
    if (this.hasPrescriptionContainerTarget) {
      this.prescriptionContainerTarget
        .querySelectorAll('input, select, textarea')
        .forEach((el) => {
          el.disabled = true;
        });
    }

    if (this.hasProductsToDeleteTarget) {
      this.productsToDeleteTarget.disabled = true;
    }

    e.target.form.submit();
  }

  submitProductAccess(e) {
    const formData = new FormData();

    const fullProductAccess =
      this.fullProductAccessTargets.find((el) => el.checked) ||
      this.fullProductAccessTargets.find((el) => el.type === 'hidden');

    const prescriptionReminder = this.prescriptionReminderTargets.find(
      (el) => el.checked,
    );
    formData.append('prescription_reminder', prescriptionReminder.value);

    let prescriptions;

    if (fullProductAccess?.dataset?.access === 'practitioner_only') {
      const idsToDelete = Array.from(
        this.prescriptionWrapperTarget.querySelectorAll(
          'select[name="bio_patient_form[product_id]"]',
        ),
      )
        .map((s) => s.value)
        .filter(Boolean);

      if (
        idsToDelete.length > 0 &&
        !confirm(
          'This will remove all existing prescriptions for this patient. Are you sure you want to continue?',
        )
      ) {
        return;
      }

      if (idsToDelete.length > 0) {
        formData.append(
          'bio_patient_form[products_to_delete]',
          JSON.stringify(idsToDelete),
        );
      }

      formData.append('bio_patient_form[prescriptions][]', '');

      prescriptions = [];
    } else {
      if (this.hasProductsToDeleteTarget && this.productsToDeleteTarget.value) {
        formData.append(
          'bio_patient_form[products_to_delete]',
          this.productsToDeleteTarget.value,
        );
      }

      prescriptions = this.prescriptionItemTargets;

      if (prescriptions.length === 1) {
        const firstPrescription = prescriptions[0];

        const productSelect = firstPrescription.querySelector(
          'select[name="bio_patient_form[product_id]"]',
        );
        const repeatsInput = this.quantityInputTargets.find((el) =>
          firstPrescription.contains(el),
        );

        if (!productSelect.value && !repeatsInput.value) {
          prescriptions = [];
          formData.append('bio_patient_form[prescriptions][]', '');
        }
      }

      for (let [index, prescription] of Array.from(prescriptions).entries()) {
        prescription
          .querySelectorAll('input, select, textarea')
          .forEach((el) => {
            if (
              !el.name ||
              ((el.type === 'radio' || el.type === 'checkbox') && !el.checked)
            )
              return;

            const nameMatch = el.name.match(/\[([^\]]+)]$/);
            if (!nameMatch) return;

            const fieldName = nameMatch[1].replace(/_\d+$/, '');

            formData.append(
              `bio_patient_form[prescriptions][${index}][${fieldName}]`,
              el.value,
            );
          });
      }
    }

    if (fullProductAccess?.dataset?.access === 'individual') {
      formData.append('individual_product_access', 'true');
    } else {
      formData.append('individual_product_access', 'false');
    }

    const url = e.target.closest('form').action;

    this.prescriptionErrorsTarget.innerHTML = '';

    fetch(url, {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    })
      .then((response) => {
        if (!response.ok) throw response;
        return response.json();
      })
      .then((data) => {
        this.clearChanges();

        if (this.hasProductAccessOptionsTarget) {
          this.originalContent = this.productAccessMarkup();
        }

        this.element
          .querySelectorAll(
            'label[for^="bio_patient_form_quantity_"] .tip__bubble',
          )
          .forEach((el) => {
            el.textContent =
              "The quantity of an existing prescription can't be changed.";
          });

        const path = window.location.pathname;

        if (path.includes('/prescriptions')) {
          window.location.href = path.replace(/\/prescriptions/, '');
        } else {
          this.toggleProductAccess();
          if (prescriptions.length > 0) {
            this.disableSavedProductFields();
          }
        }
      })
      .catch(async (data) => {
        const errors = await data.json();

        const uniqueErrors = new Set();

        if (!Array.isArray(errors['errors'])) {
          this.prescriptionErrorsTarget.insertAdjacentHTML(
            'beforeend',
            `<p>${errors['error'] || 'An unexpected error occurred'}</p>`,
          );
          return;
        }

        for (let [index, error] of errors['errors'].entries()) {
          if (Object.keys(error).length !== 0) {
            const prescription = prescriptions[index];

            for (const [key, value] of Object.entries(error)) {
              uniqueErrors.add(
                `${key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())} ${value}`,
              );

              if (key === 'product') {
                const prescriptionSelect = prescription.querySelector(
                  '#bio_patient_form_product_id_wrapper',
                );
                prescriptionSelect.classList.add('is-invalid');
              }
            }
          }
        }

        for (let error of uniqueErrors) {
          this.prescriptionErrorsTarget.insertAdjacentHTML(
            'beforeend',
            `
                <p>
                  ${error}
                </p>
              `,
          );
        }
      });
  }

  cancelProductAccess() {
    this.productAccessOptionsTarget.innerHTML = this.originalContent;

    this.connect();

    this.prescriptionErrorsTarget.innerHTML = '';
  }

  prepopulateNote(e) {
    const selectWrapper = e.target.nextElementSibling;
    const wasInvalid =
      selectWrapper && selectWrapper.classList.contains('is-invalid');

    if (selectWrapper) {
      selectWrapper.classList.remove('is-invalid');
    }

    if (wasInvalid && this.hasPrescriptionErrorsTarget) {
      this.prescriptionErrorsTarget.innerHTML = '';
    }

    const container = e.target.closest('.prescription-container');

    if (container) {
      fetch(
        `/products/${e.target.options[e.target.selectedIndex].value}/recommended_dosage`,
      )
        .then((response) => {
          return response.json();
        })
        .then((data) => {
          const container = e.target.closest('.prescription-container');
          const index = container.dataset.index;

          container.querySelector(
            `#bio_patient_form_patient_note_${index}`,
          ).value = data['adult_dosage'];
        });
    }
  }

  dpChangeActions(e) {
    if (!this.accessExpiresDateTargets.includes(e.target)) return;

    const date = e.target.value;
    const prescriptionContainer = e.target.closest('.prescription-container');
    const expiresIn = this.accessExpiresInTargets.find((el) =>
      prescriptionContainer.contains(el),
    );

    if (date === '') {
      expiresIn.selectedIndex = this.constructor.NO_EXPIRE_OPTION;
      e.target.classList.remove('is-invalid');
    } else {
      e.target.classList.toggle('is-invalid', !this.isDateValid(date));
      expiresIn.selectedIndex = -1;
    }
  }

  isDateValid(dateString) {
    const [year, month, day] = dateString.split('-').map(Number);

    const inputDate = new Date(year, month - 1, day);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return inputDate >= today;
  }

  createDivider(index) {
    const dividerDiv = document.createElement('div');
    dividerDiv.className = 'col-xs-12';
    const hrElement = document.createElement('hr');
    dividerDiv.appendChild(hrElement);
    dividerDiv.dataset['index'] = index;
    dividerDiv.dataset['name'] = 'divider';

    return dividerDiv;
  }

  clonePrescriptionDiv() {
    const prescriptionDivs = this.prescriptionDivs();
    const originalDiv = prescriptionDivs[prescriptionDivs.length - 1];
    const clonedDiv = originalDiv.cloneNode(true);
    const currentId = parseInt(originalDiv.dataset['index']);
    clonedDiv.id = `prescription-${currentId + 1}`;
    clonedDiv.dataset['index'] = currentId + 1;

    clonedDiv.querySelectorAll('label').forEach((el) => {
      el.setAttribute('for', `${el.getAttribute('for')}_${currentId + 1}`);
    });

    clonedDiv.querySelectorAll('input, textarea').forEach((el) => {
      el.classList.remove('is-invalid');
      el.id = el.id.replace(/_\d+$/, `_${currentId + 1}`);
      el.disabled = false;
      el.value = '';
    });

    clonedDiv.querySelectorAll('.tip').forEach((tip) => {
      const bubble = tip.querySelector('.tip__bubble');
      bubble.id = `${bubble.id.replace(/_\d+$/, '')}_${currentId + 1}`;
      tip.setAttribute('aria-describedby', bubble.id);
    });

    // Drop the wrappers Tom Select generated for the originals; Stimulus rebuilds
    // them when the clone is appended to the document.
    clonedDiv.querySelectorAll('.ts-wrapper').forEach((el) => el.remove());

    clonedDiv
      .querySelectorAll('.form-pos--discontinued')
      .forEach((el) => el.classList.remove('form-pos--discontinued'));
    clonedDiv
      .querySelectorAll('.discontinued-tooltip-icon')
      .forEach((el) => el.remove());

    clonedDiv.querySelectorAll('select').forEach((el) => {
      if (el.matches('[data-controller~="select"]')) {
        el.classList.remove('tomselected', 'ts-hidden-accessible');
        el.removeAttribute('tabindex');
        el.disabled = false;

        const selectedValues = [];
        this.prescriptionWrapperTarget
          .querySelectorAll('select[data-controller~="select"]')
          .forEach((select) => {
            if (select.value) {
              selectedValues.push(select.value);
            }
          });

        Array.from(el.options).forEach((option) => {
          if (option.value && selectedValues.includes(option.value)) {
            option.remove();
          }
        });

        el.selectedIndex = -1;
      } else {
        el.removeAttribute('disabled');
        el.selectedIndex = this.constructor.NO_EXPIRE_OPTION;
      }
    });

    const ordersRemainingDiv = clonedDiv.querySelector('.orders-remaining');
    if (ordersRemainingDiv) {
      ordersRemainingDiv.remove();
    }

    clonedDiv.querySelector(
      'label[for^="bio_patient_form_quantity_"] .tip__bubble',
    ).textContent =
      'Please specify a quantity greater than 0, or leave blank for unlimited repeats.';

    return clonedDiv;
  }

  prescriptionDivs() {
    return this.prescriptionItemTargets;
  }
}
